import { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import { Menu, X, ChevronDown, ArrowUpRight, Mail, Plus } from "lucide-react";
import { NAV, AREA_GROUPS, OFFICE, PARTNERS_CONTACT, MAILTO } from "../content";
import { LOGO } from "../assets";

const ease = [0.22, 1, 0.36, 1];

function useLenis() {
  const location = useLocation();
  const lenisRef = useRef(null);
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  useEffect(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [location.pathname]);
}

const Wordmark = ({ light }) => (
  <Link to="/" data-testid="logo-link" className="flex items-center group">
    <img src={light ? LOGO.white : LOGO.navy} alt="Pahl & Burgueño Advogados" className="h-8 lg:h-9 w-auto" />
  </Link>
);

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setDrawer(false); setOpen(false); }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-ink/90 backdrop-blur-md py-4 border-b border-white/10" : "py-6 border-b border-transparent"}`}
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between">
          <Wordmark light />
          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((item) =>
              item.submenu ? (
                <div key={item.to} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
                  <NavLink to={item.to} data-testid="nav-atuacao" className="flex items-center gap-1 text-[0.82rem] tracking-wide text-bone/85 hover:text-gold transition-colors">
                    {item.label} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                  </NavLink>
                  <AnimatePresence>
                    {open && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.35, ease }}
                        className="absolute right-0 top-full pt-5"
                      >
                        <div className="w-[min(560px,88vw)] bg-ink-soft border border-white/10 rounded-xl p-6 grid grid-cols-2 gap-x-8 gap-y-5 shadow-2xl">
                          {AREA_GROUPS.map((g) => (
                            <div key={g.group}>
                              <div className="eyebrow text-gold/80 mb-2 text-[0.6rem]">{g.group}</div>
                              <ul className="space-y-1.5">
                                {g.items.map((it) => (
                                  <li key={it.slug}>
                                    <Link to={`/atuacao/${it.slug}`} data-testid={`menu-area-${it.slug}`} className="text-[0.86rem] text-bone/80 hover:text-gold transition-colors link-underline">{it.title}</Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <NavLink key={item.to} to={item.to} data-testid={`nav-${item.to.replace(/\//g, "") || "home"}`}
                  className={({ isActive }) => `text-[0.82rem] tracking-wide transition-colors ${isActive ? "text-gold" : "text-bone/85 hover:text-gold"}`}>
                  {item.label}
                </NavLink>
              )
            )}
            <Link to="/contato" data-testid="header-cta" className="btn-gold !py-2.5 !px-5">Fale conosco</Link>
          </nav>
          <button data-testid="mobile-menu-toggle" className="lg:hidden text-bone" onClick={() => setDrawer(true)} aria-label="Abrir menu"><Menu /></button>
        </div>
      </header>

      <AnimatePresence>
        {drawer && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-ink-deep lg:hidden overflow-y-auto" data-testid="mobile-drawer">
            <div className="px-6 py-6 flex items-center justify-between border-b border-white/10">
              <Wordmark light />
              <button data-testid="mobile-menu-close" onClick={() => setDrawer(false)} className="text-bone"><X /></button>
            </div>
            <nav className="px-6 py-8 flex flex-col gap-6">
              {NAV.map((item) => (
                <Link key={item.to} to={item.to} className="serif text-3xl text-bone hover:text-gold transition-colors">{item.label}</Link>
              ))}
              <div className="pt-4 border-t border-white/10">
                <div className="eyebrow text-gold/70 mb-3">Áreas</div>
                {Object.values(AREA_GROUPS).flatMap((g) => g.items).map((it) => (
                  <Link key={it.slug} to={`/atuacao/${it.slug}`} className="block py-1.5 text-bone/75 hover:text-gold">{it.title}</Link>
                ))}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="relative bg-ink-deep text-bone grain overflow-hidden">
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20">
        <div className="grid lg:grid-cols-[1.4fr_1fr_1fr] gap-12">
          <div>
            <Wordmark light />
            <p className="mt-6 text-bone/55 text-sm max-w-sm leading-relaxed">{OFFICE.society}</p>
            <p className="text-bone/40 text-sm mt-1">{OFFICE.cnpj} · {OFFICE.city}</p>
            <a href={MAILTO} data-testid="footer-email" className="inline-flex items-center gap-2 mt-6 text-gold link-underline text-sm"><Mail size={15} /> {OFFICE.email}</a>
          </div>
          <div>
            <div className="eyebrow text-bone/40 mb-4">Navegação</div>
            <ul className="space-y-2.5 text-sm text-bone/70">
              <li><Link to="/escritorio" className="hover:text-gold transition-colors">O escritório</Link></li>
              <li><Link to="/atuacao" className="hover:text-gold transition-colors">Atuação</Link></li>
              <li><Link to="/socios" className="hover:text-gold transition-colors">Sócios</Link></li>
              <li><Link to="/contato" className="hover:text-gold transition-colors">Contato</Link></li>
              <li><Link to="/informacoes-legais" className="hover:text-gold transition-colors">Informações legais</Link></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow text-bone/40 mb-4">Sócios</div>
            <ul className="space-y-4 text-sm text-bone/70">
              <li>
                <div className="text-bone">{PARTNERS_CONTACT.lucas.name}</div>
                <div className="text-bone/45 text-xs">{PARTNERS_CONTACT.lucas.phone}</div>
                <a href={PARTNERS_CONTACT.lucas.linkedin} target="_blank" rel="noreferrer" className="text-gold text-xs hover:underline inline-flex items-center gap-1">LinkedIn <ArrowUpRight size={12} /></a>
              </li>
              <li>
                <div className="text-bone">{PARTNERS_CONTACT.rodrigo.name}</div>
                <div className="text-bone/45 text-xs">{PARTNERS_CONTACT.rodrigo.phone}</div>
                <a href={PARTNERS_CONTACT.rodrigo.linkedin} target="_blank" rel="noreferrer" className="text-gold text-xs hover:underline inline-flex items-center gap-1">LinkedIn <ArrowUpRight size={12} /></a>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-xs text-bone/40">
          <span>© 2026 {OFFICE.full}. Todos os direitos reservados.</span>
          <span>{OFFICE.site}</span>
        </div>
      </div>
    </footer>
  );
}

function WhatsappFloat() {
  const [o, setO] = useState(false);
  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3">
      <AnimatePresence>
        {o && (
          <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="bg-ink-soft border border-white/10 rounded-xl p-2 w-60 shadow-2xl">
            {[PARTNERS_CONTACT.lucas, PARTNERS_CONTACT.rodrigo].map((p) => (
              <a key={p.name} href={p.wa} target="_blank" rel="noreferrer" data-testid={`wa-float-${p.name.split(" ")[0].toLowerCase()}`}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors">
                <div>
                  <div className="text-bone text-sm">{p.name.split(" ")[0]}</div>
                  <div className="text-bone/40 text-xs">{p.phone}</div>
                </div>
                <ArrowUpRight size={16} className="text-gold" />
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      <button data-testid="wa-float-toggle" onClick={() => setO(!o)}
        className="h-14 w-14 rounded-full bg-gold text-ink-deep grid place-items-center shadow-2xl hover:bg-gold-bright transition-colors">
        <motion.span animate={{ rotate: o ? 45 : 0 }}><Plus /></motion.span>
      </button>
    </div>
  );
}

export default function Layout({ children }) {
  useLenis();
  return (
    <div className="min-h-screen flex flex-col bg-bone">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <WhatsappFloat />
    </div>
  );
}
